import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { isEmpty, sortedIndex } from 'lodash';
import uPlot from 'uplot';

import moment from '@splunk/moment';
import { formatDurationMicros } from './formatDuration';
import { shortenByte } from './shortenByte';
import { shortenNumber } from './shortenNumber';
import { scaleTime, TimeScale } from './scaleTime';
import type { SeriesInfo } from '../renderers/types';
import { VizTheme } from '../theme';
import type {
    AdditionalTooltipValues,
    DataSet,
    GenericDataSet,
    MarkAnnotation,
    NamedFormatters,
    NumberedFormatters,
    ScaleConfig,
    SeriesConfig,
    TickInterval,
} from '../types';
import { isTimeSeriesData } from '../renderers/uplot-utils';
import { LocaleInfo } from './formatTimestamp';

export const SECOND_TO_MICROSECOND = 1000000;
export const SECOND_TO_MILLISECOND = 1000;
export const DAY = 24 * 60 * 60;
// assume the maximum value an epoch seconds value could as 10x the current value,
// anything greater will be interpreted as an epoch milliseconds value
export const EPOCH_SECONDS_MAX_VALUE = Date.now() / 100;

type RowContext = Record<string, string | number | null>;
type TimeSeriesNormalizationResult = {
    filteredData: GenericDataSet;
    filteredIndices?: number[];
};

// normalize a time value to epoch seconds, can convert from epoch milliseconds or ISO strings
export const normalizeTimeValue = <T>(val: string | number | T): number | T => {
    if (typeof val === 'string') {
        return Date.parse(val) / 1000;
    }
    if (typeof val === 'number' && val > EPOCH_SECONDS_MAX_VALUE) {
        return val / 1000;
    }
    return val;
};

export const normalizeAndFilterTimeSeriesColumns = (data: GenericDataSet): TimeSeriesNormalizationResult => {
    const xColumn = data.columns[0] || [];
    const normalizedXValues = xColumn.map(value => normalizeTimeValue(value));
    const validIndices: number[] = [];

    normalizedXValues.forEach((value, index) => {
        if (typeof value === 'number' && Number.isFinite(value)) {
            validIndices.push(index);
        }
    });

    if (validIndices.length === normalizedXValues.length) {
        return {
            filteredData: data,
        };
    }

    const filteredColumns = data.columns.map(column => validIndices.map(index => column[index]));
    filteredColumns[0] = validIndices.map(index => normalizedXValues[index]);

    return {
        filteredData: { fields: data.fields, columns: filteredColumns } as DataSet,
        filteredIndices: validIndices,
    };
};

export const filterAdditionalTooltipValues = (
    values: AdditionalTooltipValues | undefined,
    retainedRowIndices?: number[]
): AdditionalTooltipValues | undefined => {
    if (!values || !retainedRowIndices) {
        return values;
    }

    return retainedRowIndices
        .map(index => values[index])
        .filter((value): value is RowContext => value !== undefined);
};

export function exists<T>(value: T | undefined | null): value is T {
    return value !== undefined && value !== null;
}

export function isNumber(num: number | null | undefined): num is number {
    return exists(num) && Number.isFinite(num);
}

/**
 * Applies opacity to a color string and returns rgba().
 * Supports hex (#RGB, #RGBA, #RRGGBB, #RRGGBBAA) and rgb/rgba input.
 */
export const applyOpacityToColor = (inputColor: string, opacity?: number): string => {
    if (opacity === undefined || opacity === null || Number.isNaN(opacity)) {
        return inputColor;
    }

    const clampedOpacity = Math.min(Math.max(opacity, 0), 1);
    const color = inputColor.trim();

    if (color.startsWith('#')) {
        const hex = color.slice(1);
        if (hex.length === 3 || hex.length === 4) {
            const r = Number.parseInt(hex[0] + hex[0], 16);
            const g = Number.parseInt(hex[1] + hex[1], 16);
            const b = Number.parseInt(hex[2] + hex[2], 16);
            return `rgba(${r}, ${g}, ${b}, ${clampedOpacity})`;
        }
        if (hex.length === 6 || hex.length === 8) {
            const r = Number.parseInt(hex.slice(0, 2), 16);
            const g = Number.parseInt(hex.slice(2, 4), 16);
            const b = Number.parseInt(hex.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${clampedOpacity})`;
        }
    }

    const rgbRegex = /^rgba?\((.+)\)$/i;
    const rgbMatch = rgbRegex.exec(color);
    if (rgbMatch?.[1]) {
        const parts = rgbMatch[1].split(',').map(part => part.trim());
        if (parts.length >= 3) {
            const [r, g, b] = parts;
            return `rgba(${r}, ${g}, ${b}, ${clampedOpacity})`;
        }
    }

    return inputColor;
};

/**
 * Centers a 1px tooltip line on the same integer pixel as the annotation icon across all
 * scaleFactor values. `scaleFactor` is kept in the signature for API compatibility.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const calculateTooltipLinePosition = (left: number, scaleFactor: number): number => left - 0.5;

type AnyFunction = (...args: unknown[]) => unknown;
/**
 * Creates a stable callback for a possibly dynamic callback.
 * Solution from: https://github.com/facebook/react/issues/16956#issuecomment-536636418
 */
export function useDynamicCallback(callback?: AnyFunction): AnyFunction | undefined {
    const ref = useRef<AnyFunction | undefined>(callback);
    useLayoutEffect(() => {
        ref.current = callback;
    }, [callback]);
    return useCallback((...args: Parameters<AnyFunction>) => {
        ref.current?.(...args);
    }, []);
}

export function useCreateAttachedDivElement(
    attachTo: HTMLElement | undefined,
    className: string,
    dataTestAttribute?: string
): HTMLElement {
    const iElRef = useRef<HTMLElement>(document.createElement('div'));
    useLayoutEffect(() => {
        if (!className) {
            return;
        }

        iElRef.current.className = className;

        if (dataTestAttribute) {
            iElRef.current.setAttribute('data-test', dataTestAttribute);
        }

        const el = attachTo?.querySelector<HTMLElement>(className);
        if (attachTo && !el) {
            attachTo.appendChild(iElRef.current);
        }
    }, [attachTo, className, dataTestAttribute]);
    return iElRef.current;
}

/**
 * Logs out the properties that change between render cycles. Answers the
 * "what value - if anything - caused this render to occur"
 *
 * @param name
 * @param props
 */
export const useWhyDidYouUpdate = <T extends Record<string, unknown>>(name: string, props: T): void => {
    // Get a mutable ref object where we can store props ...
    // ... for comparison next time this hook runs.
    const previousProps = useRef<T>();

    useEffect(() => {
        console.log(
            `[useWhyDidYouUpdate] ${name} - useEffect running, has previous:`,
            !!previousProps.current
        );

        if (exists(previousProps.current)) {
            // Get all keys from previous and current props
            const allKeys: (keyof T)[] = Object.keys({
                ...previousProps.current,
                ...props,
            }) as (keyof T)[];
            // Use this object to keep track of changed props
            const changesObj: Record<string, { from: unknown; to: unknown }> = {};
            // Iterate through keys
            allKeys.forEach(key => {
                // If previous is different from current
                if (exists(previousProps.current) && previousProps.current[key] !== props[key]) {
                    // Add to changesObj
                    changesObj[String(key)] = {
                        from: previousProps.current[key],
                        to: props[key],
                    };
                }
            });

            console.log(`[useWhyDidYouUpdate] ${name} - Changes detected:`, Object.keys(changesObj).length);

            // If changesObj not empty then output to console (only in development)
            if (Object.keys(changesObj).length && process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.log('[why-did-you-update]', name, changesObj);
            }
        } else {
            console.log(`[useWhyDidYouUpdate] ${name} - First render, no previous props`);
        }

        // Finally update previousProps with current props for next hook call
        previousProps.current = props;
    });
};

/**
 * Returns the fully formed SeriesConfig for the given field. Merges the `seriesConfig`
 * properties from the associated `ScaleConfig`, and the explictly defined `SeriesConfig`.
 */
export function getMergedSeriesConfigForField(
    field: string | undefined,
    seriesConfigMap?: SeriesConfig,
    scaleConfigMap?: ScaleConfig
): SeriesConfig[keyof SeriesConfig] {
    const defaults: SeriesConfig[keyof SeriesConfig] = { scale: 'y' };

    if (!exists(field)) {
        return defaults;
    }

    const seriesConfig = seriesConfigMap?.[field];
    const scale = seriesConfig?.scale || 'y';
    const scaleConfig = scaleConfigMap?.[scale];
    const mergedSeriesConfig: SeriesConfig[keyof SeriesConfig] = {
        ...defaults, // defaults
        ...scaleConfig?.seriesConfig,
        ...seriesConfig,
    };
    return mergedSeriesConfig;
}

/** Create merged config for every defined series */
export function getMergedSeriesConfig(
    seriesConfigMap?: SeriesConfig,
    scaleConfigMap?: ScaleConfig
): SeriesConfig {
    if (!seriesConfigMap) {
        return {};
    }
    return Object.keys(seriesConfigMap).reduce<SeriesConfig>((acc, field) => {
        acc[field] = getMergedSeriesConfigForField(field, seriesConfigMap, scaleConfigMap);
        return acc;
    }, {});
}

export const formatKibBytes = (bytes: number, decimals = 2): string => {
    if (!+bytes) {
        return '0B';
    }

    // Handle negative values
    if (bytes < 0) {
        return `-${formatKibBytes(-bytes, decimals)}`;
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(dm))}${sizes[i]}`;
};

export const valueFormatters: NumberedFormatters = {
    durationMicros: value => value,
    rate: value => value,
    count: value => value,
    bytes: value => value,
    percentage: value => +value.toFixed(value < 10 ? 2 : 1),
    kibBytes: value => value,
};

export const namedFormatters: NamedFormatters = {
    durationMicros: value => formatDurationMicros(value),
    rate: (value, xAxisResolution) => `${shortenNumber(value / (xAxisResolution || 1))} /s`,
    count: value => `${shortenNumber(value)}`,
    bytes: value => `${shortenByte(value)}`,
    percentage: value => `${shortenNumber(value)}%`,
    kibBytes: value => `${formatKibBytes(value, 0)}`,
};

// namedFormatters with an additional decimal place
export const scaleNamedFormatters: NamedFormatters = {
    ...namedFormatters,
    percentage: value => `${shortenNumber(value, value < 10 ? 3 : 2)}%`,
};

export const getXAxisResolution = (data: GenericDataSet): number => {
    if (isEmpty(data.columns)) {
        return 0;
    }
    const timeSeries = data.columns[0];
    const firstNonNullIndex = timeSeries.findIndex((val, i) => val !== null && timeSeries[i + 1] !== null);
    if (firstNonNullIndex === -1) {
        return 0;
    }
    const firstValue = timeSeries[firstNonNullIndex] as number;
    const secondValue = timeSeries[firstNonNullIndex + 1] as number;
    if (firstValue == null || secondValue == null) {
        return 0;
    }
    return secondValue - firstValue;
};
// legend and tooltip
export const formatValueWithSeriesConfig = (
    value: number,
    seriesConfig: SeriesConfig[string],
    data: GenericDataSet
): React.ReactNode => {
    const formatter = seriesConfig.valueFmt;
    if (!formatter) {
        return String(value);
    }
    const formatFn = typeof formatter === 'string' ? namedFormatters[formatter] : formatter;
    const xAxisResolution = getXAxisResolution(data);
    return formatFn(value, xAxisResolution);
};

// plotting on chart
export const formatValueForPlottingWithSeriesConfig = (
    value: number,
    seriesConfig: SeriesConfig[string],
    data: GenericDataSet
): React.ReactNode => {
    const formatter = seriesConfig.valueFmt;
    if (!formatter) {
        return value;
    }
    const formatFn = typeof formatter === 'string' ? valueFormatters[formatter] : formatter;
    const xAxisResolution = isTimeSeriesData(data) ? getXAxisResolution(data as DataSet) : null;
    return formatFn(value, xAxisResolution);
};

// axes
export const formatValueWithScaleConfig = (
    value: number,
    scaleConfig: ScaleConfig[string],
    xAxisResolution: number
): string => {
    const formatter = scaleConfig.tickFmt;
    if (!formatter) {
        return String(value);
    }
    const formatFn = typeof formatter === 'string' ? scaleNamedFormatters[formatter] : formatter;
    return formatFn(value, xAxisResolution);
};

export const sortDataSetByTime = (data: DataSet): { sortedData: DataSet; sortedIndices: number[] | null } => {
    const timeCol = data.columns[0] as number[];
    if (!timeCol || timeCol.length <= 1) return { sortedData: data, sortedIndices: null };
    const isSorted = timeCol.every((val, i) => i === 0 || timeCol[i - 1] <= val);
    if (isSorted) return { sortedData: data, sortedIndices: null };
    const sortedIndices = timeCol.map((_, i) => i).sort((a, b) => timeCol[a] - timeCol[b]);
    return {
        sortedData: {
            ...data,
            columns: data.columns.map(col => sortedIndices.map(i => col[i])) as DataSet['columns'],
        },
        sortedIndices,
    };
};

export const findClosest = (arr: number[], target: number): number | null => {
    let closest = null;
    if (target > arr[arr.length - 1] || target < arr[0]) {
        return closest;
    }
    const sortedIdx = sortedIndex(arr, target);
    const leftVal = arr[sortedIdx - 1];
    const rightVal = arr[sortedIdx];

    const middle = Math.floor((leftVal + rightVal) / 2);
    if (target >= middle) {
        closest = rightVal;
    } else {
        closest = leftVal;
    }

    return closest;
};

/*
  Heatmap Utils
 */
export const buildColorFunction =
    (maxDensity: number, theme: VizTheme): ((value: number) => string) =>
    (value: number): string => {
        const colors = theme.heatmap.colorScheme;
        const bucketIndex = Math.round((value / maxDensity) * (colors.length - 1));
        return colors[bucketIndex];
    };

export const timeTickFormatter = (
    tickValue: number,
    startingScale: TimeScale,
    decimalPlaces?: number
): string => {
    const { value, scale } = scaleTime(tickValue, startingScale, decimalPlaces);
    return value + scale;
};

// filterSplit checks if the current split value is a power of ten or the first subdivision
// after power of ten by checking its index position, and returns the split value if it is.
export const filterSplit = (index: number, split: number, splitsPos: number[], u: uPlot): number | null => {
    const mod = index % 9;
    if (mod === 0) {
        return split;
    }

    // Check if the first power of ten subdivision has enough space to render with no overlap
    if (mod === 1) {
        const textMetrics = u.ctx.measureText(String(split));
        const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;

        return textHeight + 1 <= Math.abs(splitsPos[index] - splitsPos[index - 1]) ? split : null;
    }

    return null;
};

export const roundToPoT = (value: number, roundFunc: (n: number) => number): number =>
    10 ** roundFunc(Math.log10(value));

export const pixelStrToNum = (str: string): number => Number(str.replace('px', ''));

export const X_AXIS_TIME_OPTIONS = {
    None: 0,
    '1Min': 60,
    '1Hour': 3600,
    '2Hours': 7200,
    '6Hours': 21600,
    '12Hours': 43200,
    '1Day': 86400,
    '2Days': 172800,
    '8Days': 691200,
};

export const markPrefix = 'mark-';

// removes the _time series and adds marks
export const buildSeriesForLegend = (
    series: SeriesInfo[],
    reversedOrder?: boolean,
    marks?: MarkAnnotation[],
    legendLabels?: string[]
): SeriesInfo[] => {
    // The _time series is currently first and we don't want to show it as a legend item so we slice it off
    let slicedSeries = series.slice(1);

    // Apply legendLabels BEFORE reversing - labels should map to series in their original order
    // Create new objects instead of mutating to avoid issues with immutable series data
    if (legendLabels && legendLabels.length > 0) {
        slicedSeries = slicedSeries.map((seriesItem, index) => {
            if (index < legendLabels.length && legendLabels[index]) {
                return {
                    ...seriesItem,
                    label: legendLabels[index],
                };
            }
            return seriesItem;
        });
    }

    if (reversedOrder) {
        slicedSeries.reverse();
    }

    // Add marks to the end of the sliced series list. They currently have to be at
    // the end so the seriesDataAccessor doesn't try and access an invalid data.column
    marks?.forEach(({ legendLabel, color }) => {
        if (!legendLabel) {
            return;
        }

        slicedSeries.push({
            field: `${markPrefix}${legendLabel}`,
            fill: color,
            label: legendLabel,
            show: true,
        });
    });

    return slicedSeries;
};

// determine tickInterval used for x-axis timestamp labelling, tickDiff (diff passed in millisec)
export const getTickInterval = (tickDiff: number): TickInterval => {
    if (tickDiff < 60000) {
        return '1s';
    }
    if (tickDiff < 3600000) {
        return '1m';
    }
    if (tickDiff < 18000000) {
        return '30m';
    }
    if (tickDiff < 43200000) {
        return '1h';
    }
    if (tickDiff < 86400000) {
        return '12h';
    }
    if (tickDiff < 604800000) {
        return '1d';
    }
    if (tickDiff < 2592000000) {
        return '7d';
    }
    return '1mo';
};

// Return the default timestamp parts for the expanded format
export const getDefaultExpandedFormatTimeStampLabelParts = (
    millisecondsEpoch: number,
    tickInterval: TickInterval,
    locale: LocaleInfo
): { time: string; day: string; month: string; year: string } => {
    let momentDate: moment.Moment;
    if (locale.serializedTimezone || locale.ianaTimezone || locale.utcOffset !== undefined) {
        if (locale.serializedTimezone) {
            const tz = moment.setDefaultSplunkTimezone(locale.serializedTimezone);
            momentDate = moment.tz(millisecondsEpoch, tz);
        } else if (locale.ianaTimezone) {
            momentDate = moment.tz(millisecondsEpoch, locale.ianaTimezone);
        } else {
            momentDate = moment.utc(millisecondsEpoch).utcOffset(locale.utcOffset);
        }
        return {
            time: tickInterval === '1s' ? momentDate.format('h:mm:ss A') : momentDate.format('h:mm A'),
            day: momentDate.format('ddd, MMM D'),
            month: momentDate.format('MMMM'),
            year: momentDate.format('YYYY'),
        };
    }
    const date = new Date(millisecondsEpoch);
    const time =
        tickInterval === '1s'
            ? date.toLocaleTimeString([], {
                  timeZone: locale.tz,
                  hour: 'numeric',
                  minute: '2-digit',
                  second: '2-digit',
              })
            : date.toLocaleTimeString([], {
                  timeZone: locale.tz,
                  hour: 'numeric',
                  minute: '2-digit',
              });
    const day = date.toLocaleDateString([], {
        timeZone: locale.tz,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });
    const month = date.toLocaleDateString([], {
        timeZone: locale.tz,
        month: 'long',
    });
    const year = date.getFullYear().toString();
    return { time, day, month, year };
};

/**
 * Applies canvas scaling for TimeSeriesChart's built-in rendering.
 *
 * TimeSeriesChart uses uPlot's built-in rendering (series, axes drawn by uPlot),
 * which requires different context scaling for high-DPI displays:
 *
 * - **DPR > 1**: Scale context by scaleFactor only to avoid double-scaling
 * - **DPR = 1**: Scale context by full amount (DPR * scaleFactor)
 *
 * @param u - uPlot instance
 * @param scaleFactor - Dashboard zoom factor (1 for fullscreen, zoom value otherwise)
 */
const applyCanvasScale = (u: uPlot): void => {
    // Guard for test environments where ctx or canvas might not be available
    if (!u.ctx?.canvas) {
        return;
    }

    const { canvas } = u.ctx;

    canvas.style.width = `${u.width}px`;
    canvas.style.height = `${u.height}px`;
};

/**
 * Creates uPlot hooks for TimeSeriesChart canvas scaling.
 *
 * IMPORTANT: This is specific to charts using uPlot's built-in series rendering.
 * REQUIRES: uPlot's pxRatio option must be set to 1.
 *
 * @param scaleFactor - Dashboard zoom factor
 * @param onReady - Optional callback invoked after canvas setup
 */
export const createCanvasScaleHooks = (
    scaleFactor: number,
    onReady?: (u: uPlot) => void
): { init: (u: uPlot) => void; setSize: (u: uPlot) => void } => ({
    init: (u: uPlot): void => {
        applyCanvasScale(u);
        if (onReady) {
            onReady(u);
        }
    },
    setSize: (u: uPlot): void => {
        applyCanvasScale(u);
    },
});
